import type {
	Cart,
	CustomerOrderDetails,
	DeliveryMethodKind,
} from "@dransay/api/models";
import { DELIVERY_METHOD_KIND } from "@dransay/api/models";
import isNil from "lodash/isNil";
import { getProductDetailsUrl } from "~/utils/routes";

/** Minimal cart shape for checkout-step ecommerce enrichment. */
export type CheckoutStepCartInput = {
	cartId?: number | null;
	totalPrice?: number | null;
	vendor?: { id: string; name: string | null } | null;
	items: Array<{
		quantity: number;
		product: {
			id: number | string;
			name: string;
			kind: string | null;
			price?: number | null;
			pzn?: string | null;
			category?: string | null;
			producerName?: string | null;
			imageUrl?: string | null;
		};
	}>;
};

export type CheckoutStepCommerceProperties = {
	cart_id?: string;
	pharmacy?: string | null;
	vendor_id?: string;
	vendor_name?: string | null;
	value?: number | null;
	currency?: string;
	shipping_method?: string;
	payment_method?: string;
	products: Array<{
		quantity: number;
		product_id: number | string;
		sku?: string | null;
		variant: string;
		category?: string | null;
		name: string;
		brand?: string | null;
		price: number | null;
		currency: string;
		url: string;
		image_url: string;
	}>;
};

const deliveryMethodMap: { [key in DeliveryMethodKind]: string } = {
	[DELIVERY_METHOD_KIND.SHIPPING]: "MAIL",
	[DELIVERY_METHOD_KIND.PICKUP]: "PICKUP",
	[DELIVERY_METHOD_KIND.COURIER]: "COURIER",
	[DELIVERY_METHOD_KIND.EXPRESS_COURIER]: "EXPRESS_COURIER",
};

function getProductImageUrl(imageUrl?: string | null): string {
	if (imageUrl && !imageUrl.includes("://")) {
		return `${window.location.origin}${imageUrl}`;
	}
	return imageUrl ?? "";
}

function mapProductsFromCartItems(
	items: CheckoutStepCartInput["items"],
	currency: string,
) {
	return items.map((item) => ({
		quantity: item.quantity,
		product_id: item.product.id,
		sku: item.product.pzn ?? null,
		// The Measurement Plan field is a string, and an accessory line has no
		// kind (OTC-120). Coerced here, at the analytics boundary, rather than in
		// the cart the checkout stores — so what is emitted stays what it is today.
		variant: item.product.kind ?? "",
		category: item.product.category ?? null,
		name: item.product.name,
		brand: item.product.producerName ?? null,
		price: !isNil(item.product?.price) ? item.product.price / 100 : null,
		currency,
		url: `${window.location.origin}${getProductDetailsUrl(String(item.product.id), item.product.name, item.product.kind)}`,
		image_url: getProductImageUrl(item.product.imageUrl),
	}));
}

/**
 * Builds Measurement Plan ecommerce fields for Checkout Step Viewed/Completed from cart.
 */
export function buildCheckoutStepCommerceFromCart(
	cart: CheckoutStepCartInput | Cart | null | undefined,
	currency: string,
): CheckoutStepCommerceProperties | null {
	if (!cart?.items?.length) {
		return null;
	}
	const items = cart.items as CheckoutStepCartInput["items"];
	return {
		cart_id: cart.cartId != null ? String(cart.cartId) : undefined,
		pharmacy: cart.vendor?.name ?? null,
		vendor_id: cart.vendor?.id != null ? String(cart.vendor.id) : undefined,
		vendor_name: cart.vendor?.name ?? null,
		value: !isNil(cart.totalPrice) ? cart.totalPrice / 100 : null,
		currency,
		products: mapProductsFromCartItems(items, currency),
	};
}

/**
 * Builds Measurement Plan ecommerce fields from a placed order (payment path).
 */
export function buildCheckoutStepCommerceFromOrder(
	order: CustomerOrderDetails,
	currency: string,
	paymentMethod?: string,
): CheckoutStepCommerceProperties {
	return {
		pharmacy: order.vendor.name ?? null,
		vendor_id: String(order.vendor.id),
		vendor_name: order.vendor.name ?? null,
		value: Math.max(0, order.orderTotal / 100),
		currency,
		shipping_method: deliveryMethodMap[order.deliveryMethod.kind],
		payment_method: paymentMethod,
		products: order.items.map((item) => ({
			quantity: item.quantity,
			// The product, not the order line -- the same correction Order Completed
			// needed. Both events fire from the same call site in one tick, so a line
			// keyed differently in each leaves nothing joining step to purchase.
			product_id: item.productId,
			sku: item.pzn,
			variant: item.kind ?? "",
			category: item.category,
			name: item.name,
			brand: item.producerName,
			price: !isNil(item.price) ? item.price / 100 : null,
			currency,
			url: `${window.location.origin}${getProductDetailsUrl(String(item.productId), item.name, item.kind)}`,
			image_url: getProductImageUrl(item.imageUrl),
		})),
	};
}
